/**
 * The AI board's visual grammar, in one place so the KPI strip, the capacity
 * panels, the estate grid, the MCP fleet and the spend charts can never disagree
 * about what a number means.
 *
 * Four things live here and must not be duplicated anywhere else in the feature:
 *
 * 1. **`access_state` copy, keyed off the generated union.** `ACCESS_COPY` is
 *    typed `Record<AiAccessState, …>`, so the *compiler* keeps it exhaustive: a
 *    sixth state added in `nova/ai/models.py` fails the build here rather than
 *    rendering as a blank card.
 *
 * 2. **The null-vs-zero readings.** This is the whole point of this board, and
 *    it is the one rule most likely to be broken by accident, so the decision is
 *    made once, in a `Reading`, and every panel renders the `Reading` rather than
 *    re-deciding. Measured on 2026-08-20: three accounts genuinely have **zero
 *    deployments**, three genuinely served **zero tokens**, and the estate has
 *    **zero alert rules and zero diagnostic settings**. Meanwhile `deployments`
 *    can be `null` because Resource Graph does not index the deployment child
 *    type. A `null` and a `0` must never render identically, and **no `null` is
 *    ever toned `success`.**
 *
 * 3. **Polarity, which is inverted here relative to every other NOVA board.**
 *    Most boards read "0 problems" as good news. Several of this board's zeros
 *    are *bad* news:
 *
 *    | Zero                              | Reading                                  |
 *    | --------------------------------- | ---------------------------------------- |
 *    | `alert_rules === 0`               | **danger** — nothing will ever page anyone |
 *    | `diagnostic_settings === 0`       | **danger** — no log leaves the account     |
 *    | `deployments === 0`               | **warning** — provisioned, holding keys, deploying nothing |
 *    | `tokens_window === 0` (measured)  | **warning** — idle, still billing          |
 *    | `headroom === 0`                  | **danger** — quota fully exhausted         |
 *    | `throttled_calls_window === 0`    | **success** — the ordinary polarity        |
 *    | `servers_unreachable === 0`       | **success**, but only when probes ran      |
 *
 *    So `problemTone` (more is worse) and `coverageTone` (zero is worse) are two
 *    different functions and a caller has to pick. There is deliberately no
 *    single "tone for a count" helper, because picking the wrong polarity is the
 *    failure this file exists to prevent.
 *
 * 4. **Lower-bound language.** Every count on this board is a floor until
 *    `coverage.is_lower_bound` says otherwise, and `coverageCaption` is the one
 *    place that sentence is written.
 */

import {
  Ban,
  BellOff,
  BellRing,
  Boxes,
  CircleAlert,
  CircleCheck,
  CircleEllipsis,
  CircleSlash,
  CloudOff,
  EyeOff,
  Gauge,
  Hourglass,
  type IconComponent,
  Info,
  OctagonAlert,
  ScrollText,
  ServerOff,
  ShieldOff,
  TriangleAlert,
  Zap,
  ZapOff,
} from "@norican/icons";

import type { AiAccount, AiCoverage, AiFinding, AiMcpServer, AiOverview, AiQuota } from "@/lib/api";
import {
  formatCount as formatCountShared,
  formatCountCompact as formatCountCompactShared,
  formatCurrency as formatCurrencyShared,
  formatCurrencyCompact as formatCurrencyCompactShared,
  formatPercent as formatPercentShared,
  heatLegend,
  type StatusTone,
} from "@/lib/charts/config";

/** The one "nothing was measured" glyph on this board, exported so tiles reuse it. */
export const EM_DASH = "—";

/** Tone accepted where a figure may also be plain volume rather than a verdict. */
export type FigureTone = StatusTone | "accent";

/* ------------------------------------------------------------------------ *
 * Access state
 * ------------------------------------------------------------------------ */

/**
 * The panel access states, projected out of the generated payload.
 *
 * Never re-typed by hand: this is what makes `ACCESS_COPY` exhaustive against
 * the backend rather than against someone's memory of it.
 */
export type AiAccessState = AiOverview["access_state"];

/**
 * The state contract every `/ai/*` payload shares — the backend's `AiPanel`
 * base, projected out of the generated type rather than re-typed. All five
 * payloads satisfy it structurally, so a shared component can take any of them
 * without knowing which route it came from.
 */
export type AiPanelState = Pick<
  AiOverview,
  | "configured"
  | "access_state"
  | "status_detail"
  | "collected_at"
  | "window"
  | "cache_ttl_sec"
  | "coverage"
  | "errors"
>;

export interface AccessStateCopy {
  /** Panel heading — names the *kind* of problem, not the symptom. */
  title: string;
  /** Card accent / `Alert` tone. */
  tone: StatusTone;
  /** Icon paired with the tone — state is never colour-alone (§3.2). */
  icon: IconComponent;
  /** Whose problem this is, in one clause. */
  owner: string;
  /** What to do about it, most direct action first. */
  remedy: readonly string[];
  /** Short label for a `Badge` when the state is named inline. */
  badge: string;
}

/**
 * What each `access_state` means, whose problem it is, and what to do.
 *
 * Unlike Defender, this board's expected state today is **`ok`**: the managed
 * identity already holds `Reader` at the tenant root, so the numbers are live on
 * first paint. The four failure states are still written out in full, because
 * "the role assignment was removed" and "Cost Management is rate-limiting" want
 * opposite responses and a shared "unavailable" card would send every operator
 * to the wrong place half the time.
 */
export const ACCESS_COPY: Record<AiAccessState, AccessStateCopy> = {
  ok: {
    title: "Reading the live Azure estate",
    tone: "success",
    icon: CircleCheck,
    owner: "Nothing to do — every figure below was measured against Azure.",
    remedy: [],
    badge: "Live",
  },
  no_credential: {
    title: "NOVA has no Azure credential",
    tone: "warning",
    icon: CloudOff,
    owner:
      "A NOVA deployment gap. Nothing was asked of Azure at all, so nothing is known about the AI estate either way — this is not an estate of zero AI accounts.",
    remedy: [
      "In Azure — assign the managed identity to the nova-api Container App, then grant it Reader at the scope the board should cover.",
      "Anywhere else — set AZURE_TENANT_ID, AZURE_CLIENT_ID and AZURE_CLIENT_SECRET for the app's own service principal.",
      "An `az login` on the host is deliberately not used: NOVA never authenticates as a person, so it can never borrow a signed-in person's wider rights.",
    ],
    badge: "No credential",
  },
  unauthorised: {
    title: "NOVA's identity is not authorised to read this",
    tone: "warning",
    icon: ShieldOff,
    owner:
      "A missing role assignment. The identity exists and authenticated; Azure refused the caller, not the address.",
    remedy: [
      "Grant NOVA's identity Reader at the scope named below — the tenant root management group is what makes the whole estate visible.",
      "A narrower grant still works and simply narrows the board: every count then covers the subscriptions that answered, and the coverage line says so.",
      "No code change and no redeploy is needed; the board lights up on its own next poll.",
    ],
    badge: "403 unauthorised",
  },
  throttled: {
    title: "Azure is rate-limiting these reads",
    tone: "warning",
    icon: Hourglass,
    owner:
      "Upstream quota, not a fault. Resource Graph throttles per-tenant and Cost Management throttles on Query Processing Units tenant-wide.",
    remedy: [
      "Transient by nature — the next poll usually succeeds and the board recovers on its own.",
      "Spend has its own much longer cache TTL for exactly this reason: a dashboard refresh must not spend the estate's cost-query budget.",
      "Nothing here is a permission problem, so granting a role would change nothing.",
    ],
    badge: "Throttled",
  },
  unavailable: {
    title: "Azure did not answer",
    tone: "danger",
    icon: Ban,
    owner:
      "Upstream, or a rejected query. Either ARM returned 5xx / did not complete, or a query NOVA sent was refused outright.",
    remedy: [
      "If it persists, check Azure Service Health for Resource Graph and Azure Monitor before touching NOVA.",
      "A consistently rejected query is a NOVA bug — raise a ticket against NOVA rather than reconfiguring anything.",
      "Nothing about the numbers this panel would have shown can be inferred from this state: they were never computed.",
    ],
    badge: "Unavailable",
  },
};

/** Whether a payload carries measured numbers, as opposed to a reason it does not. */
export function isPanelOk(panel: Pick<AiPanelState, "access_state">): boolean {
  return panel.access_state === "ok";
}

/**
 * The sentence every panel with no data carries.
 *
 * A refused read rendered as `0 AI accounts` would report a smaller, cheaper,
 * healthier estate than the real one and give no hint it was doing so — the
 * flattering direction §5 and §8 exist to forbid.
 */
export const NOT_ZERO_NOTE =
  "No measurement was taken, so this panel is blank rather than zero. Nothing here is simulated, " +
  "estimated, or carried over from an earlier read.";

/* ------------------------------------------------------------------------ *
 * Coverage — every count is a floor until proven otherwise (§8)
 * ------------------------------------------------------------------------ */

/**
 * The lower-bound sentence, written once.
 *
 * `coverage.detail` is the backend's own renderable statement and is always
 * shown verbatim; this adds the standing caveat that a count over a discovered
 * estate is a floor, and names the registry's two loud states (something
 * disappeared, something appeared) rather than letting them pass silently.
 */
export function coverageCaption(coverage: AiCoverage): string {
  const parts = [coverage.detail];
  if (coverage.is_lower_bound) {
    parts.push(
      "Every count on this page is a lower bound, not a census: a query was truncated, a " +
        "subscription was unreadable, or a per-account read failed, so the real figures are " +
        "higher by an unknown amount.",
    );
  }
  if (coverage.accounts_missing > 0) {
    parts.push(
      `${formatCount(coverage.accounts_missing)} account(s) the registry has seen are absent from ` +
        "the newest pass. Deleted, or no longer visible to this credential — those need opposite " +
        "responses, so they are shown as missing rather than dropped.",
    );
  }
  if (coverage.accounts_new > 0) {
    parts.push(
      `${formatCount(coverage.accounts_new)} account(s) appeared for the first time inside the ` +
        "new-resource window. New things are a finding, not noise.",
    );
  }
  return parts.join(" ");
}

/** Whether the coverage line is itself a finding worth an `Alert` rather than a caption. */
export function coverageIsNoteworthy(coverage: AiCoverage): boolean {
  return coverage.is_lower_bound || coverage.accounts_missing > 0 || coverage.accounts_new > 0;
}

/** Tone for the coverage callout — missing resources outrank a truncated query. */
export function coverageTone(coverage: AiCoverage): StatusTone {
  if (coverage.accounts_missing > 0) {
    return "danger";
  }
  if (coverage.is_lower_bound) {
    return "warning";
  }
  return "neutral";
}

/* ------------------------------------------------------------------------ *
 * Tone helpers — `null` is never `success`, and zero is not always good
 * ------------------------------------------------------------------------ */

/**
 * Tone where **more is worse** and a measured zero is genuinely good: throttled
 * calls, exhausted quotas, unreachable servers, missing resources.
 *
 * `null` is `neutral`, never `success`: a read that never answered is not good
 * news.
 */
export function problemTone(value: number | null | undefined): StatusTone {
  if (value == null) {
    return "neutral";
  }
  return value > 0 ? "danger" : "success";
}

/** As `problemTone`, but a non-zero count is a caution rather than a failure. */
export function cautionTone(value: number | null | undefined): StatusTone {
  if (value == null) {
    return "neutral";
  }
  return value > 0 ? "warning" : "success";
}

/**
 * Tone where **zero is the bad answer** — the inverted polarity this board needs.
 *
 * Used for coverage counts whose whole point is that they should not be zero:
 * alert rules on an account, diagnostic settings on an account, deployments
 * under an account, tokens served by an account. `0 alert rules` is bad news, so
 * it must not be toned green (§8 — a coverage gap must never read as a healthy
 * zero).
 *
 * `null` stays `neutral`, because "we could not count" is a third answer and not
 * an alarming zero either.
 */
export function presenceTone(
  value: number | null | undefined,
  severity: "danger" | "warning" = "danger",
): StatusTone {
  if (value == null) {
    return "neutral";
  }
  return value === 0 ? severity : "success";
}

/**
 * Tone for a figure that is volume rather than a verdict — tokens processed,
 * calls served, spend. High is not "bad"; it is the base every rate is a share
 * of. `null` stays neutral.
 */
export function volumeTone(value: number | null | undefined): FigureTone {
  return value == null ? "neutral" : "accent";
}

/* ------------------------------------------------------------------------ *
 * Readings — the null-vs-zero decision, made once
 * ------------------------------------------------------------------------ */

/**
 * One rendered figure: the text, the tone, a badge naming the state in words,
 * and a caption saying what it is or why there isn't one.
 *
 * Panels render a `Reading` instead of deciding for themselves, which is what
 * makes "measured zero" and "not measured" impossible to collapse by accident.
 * `unknown` is exposed so a caller can suppress a chart mark or a meter that
 * would otherwise draw a fabricated bar at zero.
 */
export interface Reading {
  /** Formatted value, or `EM_DASH` when nothing was measured. */
  text: string;
  tone: StatusTone;
  /** Short words for a `Badge` — the state is never colour-alone (§3.2). */
  badge: string;
  /** Icon paired with the tone. */
  icon: IconComponent;
  /** One line: what this value is, or why there isn't one. */
  note: string;
  /** True when nothing was measured, so `text` is an em dash. */
  unknown: boolean;
  /** True when the value is a real, measured zero — a finding in its own right. */
  measuredZero: boolean;
}

/**
 * Model deployments on an account (or a kind, or a region).
 *
 * The single most important reading on this board, because both of its unhappy
 * answers are live today:
 *
 * - **`null`** — Resource Graph does not reliably project
 *   `microsoft.cognitiveservices/accounts/deployments`, so a query that answers
 *   with no rows at all while accounts exist is *blind*, not empty. Rendering it
 *   as an account with nothing deployed would invent a fleet of empty accounts.
 * - **`0`** — the query answered and there is genuinely nothing under this
 *   account. Measured on three accounts (`poca-tst-resource`,
 *   `poca-prod-resource2`, `poca-dev-docint`). That is a finding: an account
 *   provisioned with nothing deployed either serves no purpose or failed to
 *   finish provisioning, and it holds keys either way.
 */
export function deploymentsReading(deployments: number | null): Reading {
  if (deployments == null) {
    return {
      text: EM_DASH,
      tone: "neutral",
      badge: "not counted",
      icon: CircleEllipsis,
      note:
        "The deployment list could not be read, so this is unknown — not an account with " +
        "nothing deployed. Azure Resource Graph does not index the deployment child type " +
        "reliably, and a blind query must not render as an empty account.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (deployments === 0) {
    return {
      text: "0",
      tone: "warning",
      badge: "measured zero",
      icon: Boxes,
      note:
        "Measured: nothing is deployed on this account. It was provisioned, it holds keys, it " +
        "widens the attack surface, and it serves no model. This is a finding, not a blank.",
      unknown: false,
      measuredZero: true,
    };
  }
  return {
    text: formatCount(deployments),
    tone: "success",
    badge: "measured",
    icon: Boxes,
    note: "Model deployments discovered under this account.",
    unknown: false,
    measuredZero: false,
  };
}

/**
 * Tokens an account processed in the window, read through `usage_state`.
 *
 * `usage_state` is the field that makes an idle account and an unreadable one
 * distinguishable without inspecting `errors[]`. Three of six sampled accounts
 * served zero token datapoints on 2026-08-20 — real zeros, not read failures.
 */
export function tokensReading(
  tokens: number | null,
  usageState: AiAccount["usage_state"],
): Reading {
  if (usageState === "unreadable" || tokens == null) {
    return {
      text: EM_DASH,
      tone: "neutral",
      badge: "usage unreadable",
      icon: CircleEllipsis,
      note:
        "Azure Monitor did not answer for this account, so its usage is unknown. It is not " +
        "counted as idle anywhere on this board — that is what keeps the idle count honest.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (tokens === 0) {
    return {
      text: "0",
      tone: "warning",
      badge: "measured idle",
      icon: ZapOff,
      note:
        "Measured: this account served no tokens at all in the window. It still exists, still " +
        "bills, and still holds keys. An idle account is a finding, not an absence of data.",
      unknown: false,
      measuredZero: true,
    };
  }
  return {
    text: formatCount(tokens),
    tone: "success",
    badge: "serving",
    icon: Zap,
    note: "Inference tokens processed in the window.",
    unknown: false,
    measuredZero: false,
  };
}

/**
 * An account's HTTP 429 rate.
 *
 * Three distinct answers, and the middle one is the subtle one: a rate over zero
 * calls is **undefined**, not `0%`. `no-og-foundryiq-ai` measured 33.74% (3,712
 * of 11,003 calls) on 2026-08-20 — the single most actionable number on this
 * board.
 */
export function throttleReading(
  pct: number | null,
  calls: number | null,
  warningPct: number,
): Reading {
  if (pct == null) {
    const noCalls = calls === 0;
    return {
      text: EM_DASH,
      tone: "neutral",
      badge: noCalls ? "no requests" : "rate unreadable",
      icon: noCalls ? CircleSlash : CircleEllipsis,
      note: noCalls
        ? "This account served no requests in the window, so it has no throttle rate. A rate " +
          "over zero calls is undefined, not 0% — showing 0% here would claim a clean record " +
          "for an account nothing has ever called."
        : "The throttling metric did not answer for this account, so its 429 rate is unknown. " +
          "Unknown is not zero.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (pct >= warningPct) {
    return {
      text: formatPercent(pct),
      tone: "danger",
      badge: "throttled",
      icon: OctagonAlert,
      note:
        `At or above the ${formatPercent(warningPct)} warning rate: callers are being refused ` +
        "for rate or quota limits. This is the number to act on first.",
      unknown: false,
      measuredZero: false,
    };
  }
  if (pct > 0) {
    return {
      text: formatPercent(pct),
      tone: "warning",
      badge: "some 429s",
      icon: TriangleAlert,
      note: "Some requests were refused on rate or quota limits, below the warning threshold.",
      unknown: false,
      measuredZero: false,
    };
  }
  return {
    text: formatPercent(0),
    tone: "success",
    badge: "no 429s",
    icon: CircleCheck,
    note: "Measured: no request was refused on a rate or quota limit in the window.",
    unknown: false,
    measuredZero: true,
  };
}

/**
 * Alert rules scoped at an account — a coverage reading with inverted polarity.
 *
 * `0` is the measured estate-wide state on 2026-08-20 and it is **bad news**:
 * nothing will ever page anyone about this account. It is toned `danger`, never
 * green, per §8.
 */
export function alertRulesReading(rules: number | null): Reading {
  if (rules == null) {
    return {
      text: EM_DASH,
      tone: "neutral",
      badge: "not counted",
      icon: CircleEllipsis,
      note: "The alert-rule query failed for this account, so its alerting is unknown — which is not the same as none.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (rules === 0) {
    return {
      text: "0",
      tone: "danger",
      badge: "nothing alerts",
      icon: BellOff,
      note:
        "Measured: no Azure Monitor alert rule is scoped at this account, so nothing will ever " +
        "page anyone about it — not a throttling spike, not an outage, not an exhausted quota.",
      unknown: false,
      measuredZero: true,
    };
  }
  return {
    text: formatCount(rules),
    tone: "success",
    badge: "alerting",
    icon: BellRing,
    note: "Azure Monitor alert rules scoped directly at this account.",
    unknown: false,
    measuredZero: false,
  };
}

/**
 * Diagnostic settings on an account — the other inverted-polarity coverage
 * reading. `0` means no log leaves the account at all.
 */
export function diagnosticsReading(settings: number | null): Reading {
  if (settings == null) {
    return {
      text: EM_DASH,
      tone: "neutral",
      badge: "not counted",
      icon: CircleEllipsis,
      note: "The diagnostic-settings read failed for this account, so its logging is unknown — not absent.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (settings === 0) {
    return {
      text: "0",
      tone: "danger",
      badge: "no logs leave",
      icon: EyeOff,
      note:
        "Measured: no diagnostic setting is attached, so no request log, no prompt-metadata log " +
        "and no metric leaves this account. Nothing can be investigated after the fact.",
      unknown: false,
      measuredZero: true,
    };
  }
  return {
    text: formatCount(settings),
    tone: "success",
    badge: "logging",
    icon: ScrollText,
    note: "Diagnostic settings forwarding this account's logs somewhere.",
    unknown: false,
    measuredZero: false,
  };
}

/**
 * A quota row's utilisation.
 *
 * `headroom === 0` is exhausted and `danger`; a low utilisation is fine but not
 * an achievement, so an untouched row is `neutral` rather than green — the board
 * should not congratulate itself for the ~93% of rows nobody has deployed
 * against.
 */
export function quotaReading(quota: AiQuota, warningPct: number): Reading {
  if (quota.utilisation_pct == null) {
    return {
      text: EM_DASH,
      tone: "neutral",
      badge: "limit unknown",
      icon: CircleEllipsis,
      note:
        "ARM did not report a usable limit for this quota, so its utilisation cannot be " +
        "computed. Unknown headroom is not unlimited headroom.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (quota.is_exhausted) {
    return {
      text: formatPercent(quota.utilisation_pct),
      tone: "danger",
      badge: "exhausted",
      icon: OctagonAlert,
      note: "No capacity left: nothing further can be deployed against this quota.",
      unknown: false,
      measuredZero: false,
    };
  }
  if (quota.utilisation_pct >= warningPct) {
    return {
      text: formatPercent(quota.utilisation_pct),
      tone: "warning",
      badge: "at risk",
      icon: Gauge,
      note: `At or above the ${formatPercent(warningPct)} warning line — the next deployment may not fit.`,
      unknown: false,
      measuredZero: false,
    };
  }
  if ((quota.used ?? 0) > 0) {
    return {
      text: formatPercent(quota.utilisation_pct),
      tone: "success",
      badge: "in use",
      icon: CircleCheck,
      note: "In use with headroom to spare.",
      unknown: false,
      measuredZero: false,
    };
  }
  return {
    text: formatPercent(quota.utilisation_pct),
    tone: "neutral",
    badge: "untouched",
    icon: CircleSlash,
    note:
      "Nothing is deployed against this quota. Not a problem and not an achievement — most of " +
      "the estate's quota rows look like this, which is why they are not shown by default.",
    unknown: false,
    measuredZero: true,
  };
}

/* ------------------------------------------------------------------------ *
 * MCP fleet — ARM's verdict and the probe's verdict, never collapsed
 * ------------------------------------------------------------------------ */

export type AiProbeState = AiMcpServer["probe_state"];

export interface ProbeCopy {
  label: string;
  tone: StatusTone;
  icon: IconComponent;
  /** Chart fill — a DS token or a step of a DS sequential scale, never hex (§3.1). */
  color: string;
  detail: string;
}

const dangerScale = heatLegend("danger");
const neutralScale = heatLegend("neutral");

/** The muted token every "nothing to attribute" mark wears. */
export const MUTED_FILL = "var(--norican-bg-muted)";

/**
 * The four probe verdicts.
 *
 * `http_error` is the MCP-specific wrinkle: an endpoint speaking Streamable HTTP
 * answers a plain `GET` with 405 or 406 — alive and correct — so the status code
 * is reported rather than the verdict being overstated. `not_probed` is a real
 * answer and is **not** a healthy zero.
 */
export const PROBE_COPY: Record<AiProbeState, ProbeCopy> = {
  serving: {
    label: "Serving",
    tone: "success",
    icon: CircleCheck,
    color: "var(--norican-success)",
    detail: "Answered below HTTP 400.",
  },
  http_error: {
    label: "HTTP error",
    tone: "warning",
    icon: TriangleAlert,
    color: "var(--norican-warning)",
    detail:
      "Answered with 4xx or 5xx. A 405 or 406 here means alive but answering the wrong method — " +
      "correct behaviour for Streamable HTTP, so the status code is shown rather than a verdict guessed.",
  },
  unreachable: {
    label: "Unreachable",
    tone: "danger",
    icon: ServerOff,
    color: dangerScale[4] ?? "var(--norican-danger)",
    detail: "Nothing answered at all. This is not a healthy zero.",
  },
  not_probed: {
    label: "Not probed",
    tone: "neutral",
    icon: CircleEllipsis,
    color: MUTED_FILL,
    detail:
      "Identified but with no resolvable URL, or probing is disabled — so no probe verdict " +
      "exists. Unknown, not down.",
  },
};

/** How a candidate host was identified as an MCP server, in words. */
export function discoverySignalLabel(signal: AiMcpServer["discovery_signal"]): string {
  switch (signal) {
    case "tag":
      return "Tagged as an MCP server";
    case "name_pattern":
      return "Matched a configured name pattern";
    case "configured":
      return "Named in NOVA's configuration";
  }
}

/**
 * ARM's control-plane verdict, kept strictly separate from the probe.
 *
 * `is_running === null` means ARM reported no state at all — unknown, not
 * stopped — so it is never toned as a failure.
 */
export function armStateReading(isRunning: boolean | null, armState: string | null): Reading {
  if (isRunning == null) {
    return {
      text: armState ?? EM_DASH,
      tone: "neutral",
      badge: "state unknown",
      icon: CircleEllipsis,
      note: "ARM reported no running state for this host. Unknown, not stopped.",
      unknown: true,
      measuredZero: false,
    };
  }
  if (isRunning) {
    return {
      text: armState ?? "Running",
      tone: "success",
      badge: "running",
      icon: CircleCheck,
      note: "ARM reports the host as running. Whether it answers is the probe's question, not this one.",
      unknown: false,
      measuredZero: false,
    };
  }
  return {
    text: armState ?? "Stopped",
    tone: "danger",
    badge: "not running",
    icon: ServerOff,
    note: "ARM does not report a running state, so the host itself is down — a different ticket from a host that runs and answers 500.",
    unknown: false,
    measuredZero: false,
  };
}

/* ------------------------------------------------------------------------ *
 * Findings — generated by the backend from thresholds, rendered here
 * ------------------------------------------------------------------------ */

export type AiFindingSeverity = AiFinding["severity"];

export interface FindingCopy {
  label: string;
  tone: StatusTone;
  icon: IconComponent;
  /** Sort weight, worst first. */
  rank: number;
}

export const FINDING_COPY: Record<AiFindingSeverity, FindingCopy> = {
  critical: { label: "Critical", tone: "danger", icon: OctagonAlert, rank: 0 },
  warning: { label: "Warning", tone: "warning", icon: TriangleAlert, rank: 1 },
  info: { label: "For information", tone: "neutral", icon: Info, rank: 2 },
};

/**
 * A finding's category, as a label.
 *
 * `category` is a free string on the wire on purpose (§8 — no enum over live
 * data), so an unrecognised category passes through as itself rather than being
 * folded into `unknown` or raising.
 */
const CATEGORY_LABEL: Record<string, string> = {
  quota: "Quota",
  throttling: "Throttling",
  usage: "Usage",
  observability: "Observability",
  discovery: "Discovery",
  mcp: "MCP",
};

/** Label for a finding category; unknown values pass through untouched. */
export function findingCategoryLabel(category: string): string {
  return CATEGORY_LABEL[category] ?? category;
}

/** Findings worst-first, stable within a severity. */
export function sortedFindings(findings: readonly AiFinding[]): AiFinding[] {
  return [...findings].sort(
    (a, b) => FINDING_COPY[a.severity].rank - FINDING_COPY[b.severity].rank,
  );
}

/* ------------------------------------------------------------------------ *
 * Account-kind colours — categorical identity, fixed assignment (§3.3)
 * ------------------------------------------------------------------------ */

/**
 * The muted token an `unknown` kind wears.
 *
 * `kind` is whatever string ARM returned and is **never** validated against a
 * list: `AIServices`, `OpenAI` and `FormRecognizer` are what the estate holds
 * today, and a kind Microsoft ships next month has to appear on this board with
 * no code change. So the colour assignment is derived from the payload's own
 * kinds, ordered alphabetically, and only the explicit `unknown` bucket is
 * special-cased.
 */
export const UNKNOWN_KIND = "unknown";

/* ------------------------------------------------------------------------ *
 * Formatters — every one renders `null` as an em dash
 * ------------------------------------------------------------------------ */

/** Thousands-separated integer, or an em dash. Never `0` for "unknown". */
export function formatCount(value: number | null | undefined): string {
  return formatCountShared(value);
}

/** Compact count for axis ticks and dense tiles — `12.4K`, `1.2M`. */
export function formatCountCompact(value: number | null | undefined): string {
  return formatCountCompactShared(value);
}

/** A 0–100 value as a percentage with one decimal, or an em dash. */
export function formatPercent(value: number | null | undefined): string {
  return formatPercentShared(value);
}

/**
 * A money amount in the currency Cost Management reported.
 *
 * The currency is a payload field, never a constant: the AI estate bills in
 * **DKK** today and a hardcoded `EUR` would silently misprice the whole board.
 * `null` currency falls back to a plain number rather than guessing.
 */
export function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
): string {
  return formatCurrencyShared(value, currency ?? null);
}

/** Compact money for axis ticks and KPI tiles. */
export function formatMoneyCompact(
  value: number | null | undefined,
  currency: string | null | undefined,
): string {
  return formatCurrencyCompactShared(value, currency ?? null);
}

/**
 * A quota's `used / limit` as one string — `600 / 670` — or an em dash per side.
 *
 * Rendered as a pair rather than only a percentage because the pair is what an
 * operator needs to decide whether the next deployment fits: 89.55% of 670 and
 * 89.55% of 8 are very different situations.
 */
export function formatRatio(used: number | null, limit: number | null): string {
  if (used == null && limit == null) {
    return EM_DASH;
  }
  return `${formatQuotaNumber(used)} / ${formatQuotaNumber(limit)}`;
}

/**
 * A quota figure. Quota units arrive as floats but are whole numbers in
 * practice, so a fraction is shown only when there really is one — rounding
 * `0.5` to `1` would claim capacity that is not there.
 */
export function formatQuotaNumber(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** A probe round-trip in milliseconds, or an em dash. */
export function formatMs(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ms`;
}

/** An `N of M` string where either side may be unknown. */
export function formatOfTotal(shown: number, total: number | null | undefined): string {
  return `${formatCount(shown)} of ${formatCount(total)}`;
}

/**
 * Coerce a Recharts tooltip value to a number, or `null` when there is none.
 *
 * `Chart.TooltipContent` types its `formatter` as
 * `(value: number | string | undefined) => ReactNode` and calls it
 * unconditionally, so a series with no value at the hovered X arrives as
 * `undefined`. `Number(undefined)` is `NaN` and — worse — `Number(null)` is `0`,
 * which renders a fabricated zero. Both funnel into `null` here so the null-safe
 * formatters can do what they were written to do: render the gap as a gap (§5).
 *
 * **Never wrap a value in `Number()` on the way into a formatter; use this.**
 * That exact mistake put four fake numbers into the Dynamics tooltips.
 */
export function tooltipValue(value: number | string | undefined): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const DAY_WINDOW_PATTERN = /^P(\d+)D$/;
const HOUR_WINDOW_PATTERN = /^PT(\d+)H$/;

/**
 * An ISO 8601 window as a caption — `P7D` → `last 7 days`.
 *
 * Every panel states its own window because they genuinely differ: usage
 * metrics cover a rolling window while quota, deployments and observability are
 * point-in-time reads of the control plane. None of them is a filter anyone can
 * change from here, which is why this board has no `DateRangePicker` — a control
 * that changed nothing would be a lie.
 */
export function formatWindow(window: string): string {
  if (window === "PT0S") {
    return "as it stands now — a point-in-time read, not a window";
  }
  const days = DAY_WINDOW_PATTERN.exec(window);
  if (days?.[1]) {
    const count = Number(days[1]);
    return `last ${count} ${count === 1 ? "day" : "days"}`;
  }
  const hours = HOUR_WINDOW_PATTERN.exec(window);
  if (hours?.[1]) {
    const count = Number(hours[1]);
    return `last ${count} ${count === 1 ? "hour" : "hours"}`;
  }
  return window;
}

/** How stale a snapshot has to be before the `Freshness` dot escalates. */
export const STALE_MULTIPLIER_MS = 2000;

/** `Freshness` staleness threshold from a payload's own cache TTL. */
export function staleAfterMs(cacheTtlSec: number | null | undefined): number | undefined {
  return cacheTtlSec != null && cacheTtlSec > 0 ? cacheTtlSec * STALE_MULTIPLIER_MS : undefined;
}

/** A neutral ramp step, for sequential fills that are magnitude rather than status. */
export function neutralFill(step: number): string {
  return neutralScale[Math.min(Math.max(step, 0), neutralScale.length - 1)] ?? MUTED_FILL;
}

/** A danger ramp step, for the "worst N" bars where magnitude *is* severity. */
export function dangerFill(step: number): string {
  return (
    dangerScale[Math.min(Math.max(step, 0), dangerScale.length - 1)] ?? "var(--norican-danger)"
  );
}

/** The glyph a "nothing measured" cell wears, re-exported so grids reuse it. */
export const UNKNOWN_ICON: IconComponent = CircleEllipsis;

/** The glyph a lower-bound count wears. */
export const LOWER_BOUND_ICON: IconComponent = CircleAlert;
